'use client';

import React from 'react';
import { Button, Tooltip } from '@heroui/react';
import { useTranslations } from '../../../../contexts/translation.context';
import {
  FilterSelect,
  ModelSearchSelect,
  OfferFilterSelect,
  ResourcesFilterSelect,
  FilterOption,
  ResponsiveTooltip,
} from '../../../../components/ui';
import {
  ResetFiltersIc,
  AccountIcon,
  OfferFilterIc,
  FilterSourceCircleIc,
  UserFilterIc,
} from '../../../../components/icons';
import { FiltersProps } from '../types';
import { getSortingOptions, getOwnerOptions } from '../constants';

const FiltersSection: React.FC<FiltersProps> = ({
  filters,
  availableModels,
  isLoading,
  hasActiveFilters,
  ownerCounts,
  onFilterChange,
  onResetFilters,
  onModelSearchChange,
}) => {
  const { t } = useTranslations();

  const sortingOptions = getSortingOptions(t);
  const ownerOptions = getOwnerOptions(t, ownerCounts);

  const modelOptions: FilterOption[] = availableModels.map((model) => ({
    key: model.id,
    avatar: model.avatar,
    username: model.username,
    name: model.name,
    label: `${model.name} (@${model.username})`,
  }));

  return (
    <div className="mb-6 flex items-center gap-4 md:flex-wrap max-md:h-10 [&>*]:shrink-0 max-md:overflow-x-auto max-md:px-4 max-md:-mx-4 scroller-none">
      {/* Owner Filter */}
      <FilterSelect
        placeholder={t('partner.revshare.offers.filters.owner') || 'Владелец'}
        options={ownerOptions}
        selectedKey={filters.owner}
        onSelectionChange={(value) => onFilterChange('owner', value)}
        variant="campaign"
        autoWidth={true}
        startContent={<UserFilterIc />}
      />

      {/* Sort */}
      <FilterSelect
        placeholder={t('partner.revshare.offers.filters.sorting') || 'Сортировка'}
        options={sortingOptions}
        selectedKey={filters.sortBy}
        onSelectionChange={(value) => onFilterChange('sortBy', value)}
        variant="campaign"
        autoWidth={true}
        popoverWidth={240}
        startContent={
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
            <path
              fill="none"
              stroke="#A8A8B0"
              strokeLinecap="round"
              strokeWidth="1.5"
              d="M22 7H2m17 5H5m11 5H8"
            />
          </svg>
        }
        sortDirection={filters.sortBy ? filters.sortDirection : null}
        onSortDirectionChange={(dir) => onFilterChange('sortDirection', dir)}
      />

      {/* Account (Model) */}
      <ModelSearchSelect
        placeholder={t('partner.revshare.offers.filters.onlyfansAccount') || 'Аккаунт'}
        options={modelOptions}
        selectedKey={filters.model}
        onSelectionChange={(value) => onFilterChange('model', value)}
        onSearchChange={onModelSearchChange}
        isLoading={isLoading}
        startContent={<AccountIcon className="text-neutral-8 stroke-neutral-8" size={20} />}
        variant="campaign"
        autoWidth={true}
        showAdditionalFilters={true}
        priceFilter={filters.price || ''}
        onPriceFilterChange={(value) => onFilterChange('price', value)}
        topCreatorsValue={filters.topCreators || ''}
        onTopCreatorsChange={(value) => onFilterChange('topCreators', value)}
        t={t}
      />

      {/* Offer Type */}
      <OfferFilterSelect
        placeholder={t('partner.revshare.offers.filters.offer') || 'Оффер'}
        commission={filters.offerCommission || ''}
        guaranteed={filters.guaranteed || false}
        onCommissionChange={(value) => onFilterChange('offerCommission', value)}
        onGuaranteedChange={(value) => onFilterChange('guaranteed', value)}
        variant="campaign"
        autoWidth={true}
        startContent={<OfferFilterIc size={20} />}
        t={t}
      />

      {/* Sources */}
      <ResourcesFilterSelect
        placeholder={t('partner.revshare.offers.filters.resources') || 'Ресурсы'}
        allowedSource={filters.allowedSource || ''}
        excludedSource={filters.excludedSource || ''}
        onAllowedSourceChange={(source) => onFilterChange('allowedSource', source)}
        onExcludedSourceChange={(source) => onFilterChange('excludedSource', source)}
        startContent={<FilterSourceCircleIc />}
        variant="campaign"
        autoWidth={true}
        t={t}
      />

      {!hasActiveFilters && <div className="shrink-0 w-10 h-2 md:hidden"></div>}
      {hasActiveFilters && (
        <Tooltip
          content={t('partner.revshare.offers.filters.resetTooltip') || 'Сбросить фильтры'}
          placement="top"
        >
          <Button
            //@ts-ignore
            size="xs"
            className="h-9 rounded-2 !w-9 p-0 bg-auth-surface shrink-0"
            onPress={onResetFilters}
          >
            <ResetFiltersIc />
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

export default FiltersSection;
